// The read + confirm layer for the active/passive sleeve roster (migration 0003v).
//
// WHY A UI EXISTS FOR THIS AT ALL. confirm_sleeve() opens with
//     IF _uid IS NULL THEN RAISE 'confirming a sleeve must be done by a named human'
// and there is no way to satisfy that from a server session: a service-role client has no
// auth.uid(), and the Supabase SQL editor does not supply one either. Forging one with
// `set_config('request.jwt.claims', ...)` would be the same act as the authorisation bypass
// closed by migration 20260820173941 — a caller asserting an identity the platform never
// issued. A synthetic "bot" staff account would satisfy the letter of the check and defeat
// its purpose, since the roster is an input to a document a named human signs.
//
// So the only honest way to confirm a sleeve is for the person confirming it to be signed in
// and click. That is what this hook is for, and it is why the tab shows both legs of each
// pairing next to its button: the control is the human agreeing that THIS fund is the active
// expression of THAT benchmark, not the click.
//
// WHAT THE SERVER RE-CHECKS regardless of anything here (measured from pg_get_functiondef):
//   * auth.uid() IS NOT NULL                      -> 42501, named human
//   * Investment OR Operations OR admin           -> 42501
//   * >= 1 active leg carrying a holding_ref      -> check_violation; without one the sleeve
//                                                    can never produce a weight and would
//                                                    publish not_found while reading "ready"
//   * >= 1 benchmark leg                          -> check_violation
//   * the sleeve exists                           -> check_violation
// A disabled button here is UX. The RPC is the wall.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type SleeveKind = "selection" | "allocation";
export type SleeveSide = "active" | "benchmark";

export interface SleeveLeg {
  sleeve: string;
  side: SleeveSide;
  return_ref: string;
  holding_ref: string | null;
  fixed_weight: number | null;
  note: string | null;
}

export interface Sleeve {
  sleeve: string;
  kind: SleeveKind;
  label: string;
  confirmed_by: string | null;
  confirmed_at: string | null;
  note: string | null;
  legs: SleeveLeg[];
}

export const SLEEVES_QUERY_KEY = ["reporting_sleeves"] as const;

export function useSleeves() {
  return useQuery({
    queryKey: SLEEVES_QUERY_KEY,
    queryFn: async (): Promise<Sleeve[]> => {
      // Two plain selects rather than a PostgREST embed. The embed would depend on the FK
      // being named the way PostgREST expects, and a rename would degrade to "no legs" — which
      // renders as a sleeve that cannot be confirmed, i.e. a data problem wearing the costume
      // of a permissions problem. Both tables carry the same RLS predicate, so an unauthorised
      // reader gets two empty sets and the tab says so.
      const [sleeves, legs] = await Promise.all([
        (supabase as any)
          .from("reporting_sleeves")
          .select("sleeve, kind, label, confirmed_by, confirmed_at, note")
          .order("kind")
          .order("sleeve"),
        (supabase as any)
          .from("reporting_sleeve_legs")
          .select("sleeve, side, return_ref, holding_ref, fixed_weight, note"),
      ]);
      if (sleeves.error) throw sleeves.error;
      if (legs.error) throw legs.error;

      const bySleeve = new Map<string, SleeveLeg[]>();
      for (const l of (legs.data ?? []) as SleeveLeg[]) {
        const list = bySleeve.get(l.sleeve) ?? [];
        list.push(l);
        bySleeve.set(l.sleeve, list);
      }
      return ((sleeves.data ?? []) as Omit<Sleeve, "legs">[]).map((s) => ({
        ...s,
        legs: bySleeve.get(s.sleeve) ?? [],
      }));
    },
  });
}

export function useConfirmSleeve() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ sleeve, note }: { sleeve: string; note?: string | null }) => {
      const { data, error } = await (supabase as any).rpc("confirm_sleeve", {
        _sleeve: sleeve,
        _note: note ?? null,
      });
      if (error) throw error;
      return data as { sleeve: string; confirmed_by: string; confirmed_at: string };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SLEEVES_QUERY_KEY });
    },
  });
}

/**
 * Would confirm_sleeve refuse this sleeve on structural grounds?
 *
 * Mirrors the RPC's two leg checks so the tab can say WHY a button is disabled instead of
 * making the user click and read a Postgres error. It is deliberately the same two conditions
 * and nothing more: a client-side check that is STRICTER than the server teaches people the
 * wrong rule, and one that is looser produces a click that fails.
 */
export function sleeveBlockReason(s: Sleeve): string | null {
  const activeWithHolding = s.legs.filter((l) => l.side === "active" && l.holding_ref);
  if (activeWithHolding.length === 0) {
    return "no active leg carries a holding_ref, so this sleeve could never produce a weight";
  }
  if (s.legs.filter((l) => l.side === "benchmark").length === 0) {
    return "no benchmark leg — there is nothing to measure the active leg against";
  }
  return null;
}
